import React, { useEffect, lazy, Suspense } from 'react';
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './Components/ProtectedRoute';
import ProtectedProRoute from './Components/ProtectedProRoute';
import ErrorBoundary from './Components/ErrorBoundary';
import OnboardingFlow from './Components/OnboardingFlow';
import NotFoundPage from './pages/NotFoundPage';
import Lab from './lab';
import Inventory from './labcomponents/Inventory';
import RecipesPage from './labcomponents/RecipesPage';
import GeneratorPage from './labcomponents/GeneratorPage';
import PrintCenter from './labcomponents/PrintCenter';
import BatchesPage from './labcomponents/BatchesPage';
import CalendarPage from './labcomponents/CalendarPage';
import RecipeTemplates from './labcomponents/RecipeTemplates';
import SalesDashboard from './labcomponents/SalesDashboard';
import LabelGenerator from './labcomponents/LabelGenerator';
import LabDashboard from './labcomponents/LabDashboard';
import DataBackup from './labcomponents/DataBackup';
import SettingsPage from './labcomponents/SettingsPage';
import UsageReports from './labcomponents/UsageReports';
import Notifications from './labcomponents/Notifications';
import LabNotes from './labcomponents/LabNotes';
import QuickBatch from './labcomponents/QuickBatch';
import PricingPage from './labcomponents/PricingPage';
import CommunityPage from './labcomponents/CommunityPage';
import PublicRecipesPage from './labcomponents/PublicRecipesPage';
import FeatureRequestPage from './labcomponents/FeatureRequestPage';
import ContactPage from './labcomponents/ContactPage';
import PrivacyPage from './labcomponents/PrivacyPage';
import TermsPage from './labcomponents/TermsPage';
import RefundPolicyPage from './labcomponents/RefundPolicyPage';
import ShippingPolicyPage from './labcomponents/ShippingPolicyPage';
import BlogPage from './labcomponents/BlogPage';
import AddPurchasePage from './labcomponents/AddPurchasePage';
import MyPurchasesPage from './labcomponents/MyPurchasesPage';
import SuppliersPage from './labcomponents/SuppliersPage';
import PackagingPage from './labcomponents/PackagingPage';
import MeltPourCalculator from './labcomponents/MeltPourCalculator';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import AuthActionPage from './pages/AuthActionPage';
import HomePage from './pages/HomePage';
import HelpPage from './pages/HelpPage';
import ProfilePage from './pages/ProfilePage';
import PublicProfilePage from './pages/PublicProfilePage';
import LabNavbar from './labcomponents/LabNavbar';
import { trackPageView } from './utils/analytics';
// Loaded after every page stylesheet so signed-in pages share the homepage's look.
import './styles/app-theme.css';

const AdminPanel = lazy(() => import('./labcomponents/AdminPanel'));

function LegacyLabRoute() {
  const location = useLocation();
  const path = location.pathname.replace(/^\/lab(?=\/|$)/, '') || '/calculator';
  return <Navigate to={`${path}${location.search}${location.hash}`} replace />;
}

// Home route that shows HomePage for guests, Dashboard for logged-in users
function HomeRoute() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }
  
  return user ? (user.emailVerified ? <LabDashboard /> : <Navigate to="/verify-email" replace />) : <HomePage />;
}

function App() {
  const location = useLocation();

  useEffect(() => {
    // Scroll to top on route change and send GA pageview
    window.scrollTo(0, 0);
    trackPageView(location.pathname);
  }, [location]);

  return (
    <AuthProvider>
      <ErrorBoundary>
      {!['/admin'].includes(location.pathname) && <LabNavbar />}
      <OnboardingFlow />
      <main className="sl-main" style={{ minHeight: '100vh' }}>
        <Suspense fallback={<div className="sl-page mypadding" role="status">Loading page...</div>}>
        <Routes>
          {/* Home - shows HomePage for guests, Dashboard for logged-in users */}
          <Route path='/' element={<HomeRoute />} />
          
          {/* Auth routes */}
          <Route path='/login' element={<LoginPage />} />
          <Route path='/signup' element={<SignupPage />} />
          <Route path='/verify-email' element={<EmailVerificationPage />} />
          <Route path='/auth/action' element={<AuthActionPage />} />
          <Route path='/profile' element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path='/profile/:userId' element={<PublicProfilePage />} />
          <Route path='/:username' element={<PublicProfilePage />} />
          
          {/* Public routes - accessible without login */}
          <Route path='/pricing' element={<PricingPage />} />
          <Route path='/contact' element={<ContactPage />} />
          <Route path='/help' element={<HelpPage />} />
          <Route path='/lab/*' element={<LegacyLabRoute />} />
          <Route path='/my-tickets' element={<Navigate to="/contact?tab=tickets" replace />} />
          <Route path='/privacy' element={<PrivacyPage />} />
          <Route path='/terms' element={<TermsPage />} />
          <Route path='/refund-policy' element={<RefundPolicyPage />} />
          <Route path='/shipping-policy' element={<ShippingPolicyPage />} />
          <Route path='/blog' element={<BlogPage />} />
          <Route path='/blog/:postId' element={<BlogPage />} />
          
          {/* Calculator - PUBLIC (like SoapMakingFriend) */}
          <Route path='/calculator' element={<Lab />} />
          <Route path='/melt-and-pour' element={<MeltPourCalculator />} />
          <Route path='/recipes/public' element={<PublicRecipesPage />} />
          <Route path='/templates' element={<RecipeTemplates />} />
          
          {/* Community - PUBLIC but posting requires login */}
          <Route path='/community' element={<CommunityPage />} />
          <Route path='/feature-requests' element={<FeatureRequestPage />} />
          
          {/* Protected routes - require login only (FREE features) */}
          <Route path='/dashboard' element={<ProtectedRoute><LabDashboard /></ProtectedRoute>} />
          <Route path='/inventory' element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
          <Route path='/inventory/purchase' element={<ProtectedRoute><AddPurchasePage /></ProtectedRoute>} />
          <Route path='/inventory/purchases' element={<ProtectedRoute><MyPurchasesPage /></ProtectedRoute>} />
          <Route path='/inventory/suppliers' element={<ProtectedRoute><SuppliersPage /></ProtectedRoute>} />
          <Route path='/inventory/packaging' element={<ProtectedRoute><PackagingPage /></ProtectedRoute>} />
          <Route path='/recipes' element={<ProtectedRoute><RecipesPage /></ProtectedRoute>} />
          <Route path='/batches' element={<ProtectedRoute><BatchesPage /></ProtectedRoute>} />
          <Route path='/calendar' element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
          <Route path='/notifications' element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
          
          {/* PRO routes - require login AND Pro subscription */}
          <Route path='/generator' element={<ProtectedProRoute feature="Recipe Generator"><GeneratorPage /></ProtectedProRoute>} />
          <Route path='/print' element={<ProtectedProRoute feature="Print Center"><PrintCenter /></ProtectedProRoute>} />
          <Route path='/sales' element={<ProtectedProRoute feature="Sales Dashboard"><SalesDashboard /></ProtectedProRoute>} />
          <Route path='/labels' element={<ProtectedProRoute feature="Label Generator"><LabelGenerator /></ProtectedProRoute>} />
          <Route path='/backup' element={<ProtectedProRoute feature="Data Backup & Export"><DataBackup /></ProtectedProRoute>} />
          <Route path='/settings' element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path='/reports' element={<ProtectedProRoute feature="Usage Reports & Analytics"><UsageReports /></ProtectedProRoute>} />
          <Route path='/notes' element={<ProtectedProRoute feature="Lab Notes"><LabNotes /></ProtectedProRoute>} />
          <Route path='/quickbatch' element={<ProtectedProRoute feature="Quick Batch"><QuickBatch /></ProtectedProRoute>} />
          
          {/* Admin routes */}
          <Route path='/admin' element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
          
          {/* 404 — catch all unmatched routes */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </main>
      </ErrorBoundary>
    </AuthProvider>
  );
}

export default App;
